import { z } from 'zod';
import { Address, ParticipantVersion } from './participants.js';
// ---- Rule sets (decision 2A: effective-dated JSON Schema per device class) ---
export const DeviceClassId = z.string().regex(/^[a-z][a-z0-9_]{1,39}$/, 'lower-case identifier, e.g. battery');
export const EntityKind = z.enum(['model', 'unit', 'installation']);
const JsonSchema = z.record(z.unknown());
/** JSON Schema applied to the class-specific `attributes` of each entity kind. */
export const FieldSchemas = z.object({
    /** JSON Schema for the class-specific attributes of a model. @metadata business */
    model: JsonSchema.default({}),
    /** JSON Schema for the class-specific attributes of a unit. @metadata business */
    unit: JsonSchema.default({}),
    /** JSON Schema for the class-specific attributes of an installation. @metadata business */
    installation: JsonSchema.default({}),
});
/**
 * Field combinations that must be unique within a device class. Names refer to
 * core fields (`modelNumber`, `serial`, `nmi`) or `attributes.<name>`.
 */
export const DUPLICATE_KEY_CORE_FIELDS = {
    model: ['modelNumber', 'name', 'manufacturerId'],
    unit: ['serial', 'batch', 'modelId'],
    installation: ['nmi', 'unitId', 'installerParticipantId'],
};
const ATTRIBUTE_KEY = /^attributes\.[A-Za-z_][A-Za-z0-9_]*$/;
const keyNames = (core) => z.array(z.array(z.string().refine((name) => core.includes(name) || ATTRIBUTE_KEY.test(name), {
    message: `must be one of ${core.join(', ')} or attributes.<name>`,
})).min(1)).default([]);
export const DuplicateKeys = z.object({
    /** Field combinations that must be unique across the class's models. @metadata business */
    model: keyNames(DUPLICATE_KEY_CORE_FIELDS.model),
    /** Field combinations that must be unique across the class's units. @metadata business */
    unit: keyNames(DUPLICATE_KEY_CORE_FIELDS.unit),
    /** Field combinations that must be unique across the class's installations. @metadata business */
    installation: keyNames(DUPLICATE_KEY_CORE_FIELDS.installation),
});
const IsoDate = z.string().date('valid YYYY-MM-DD date required');
export const DeviceClassRule = z.object({
    /** Surrogate id of this rule version. @metadata technical @source register.device_class_rules.id @transform bigint to number */
    id: z.number().int(),
    /** Device class the rule set governs, e.g. battery. @metadata business @source register.device_class_rules.device_class */
    deviceClass: DeviceClassId,
    /** Version of the class's rule set; models, units and installations record the version they were validated against. @metadata business @source register.device_class_rules.version */
    version: z.number().int(),
    /** Date from which this version applies. @metadata business @source register.device_class_rules.valid_from */
    validFrom: IsoDate,
    /** Date this version stopped applying; null while it is current. @metadata business @source register.device_class_rules.valid_to */
    validTo: IsoDate.nullable(),
    /** Human-readable name of the device class. @metadata business @source register.device_class_rules.display_name */
    displayName: z.string(),
    /** JSON Schema the class-specific attributes of each entity kind must satisfy. @metadata business @source register.device_class_rules.field_schema @transform parsed through FieldSchemas; a missing kind becomes {} */
    fieldSchema: FieldSchemas,
    /** Field combinations that must be unique within the class, per entity kind. @metadata business @source register.device_class_rules.duplicate_keys @transform parsed through DuplicateKeys; a missing kind becomes [] */
    duplicateKeys: DuplicateKeys,
    /** User who created this version. @metadata technical @source register.device_class_rules.created_by */
    createdBy: z.string().nullable(),
    /** Display name of the user who created this version. @metadata technical @source iam.users.display_name via register.device_class_rules.created_by @transform left join; null if the user is not found */
    createdByName: z.string().nullable(),
    /** When this version was recorded. @metadata technical @source register.device_class_rules.created_at @transform timestamptz as an ISO 8601 UTC string */
    createdAt: z.string(),
    /** Why the rule set was changed. @metadata business @source register.device_class_rules.change_reason */
    changeReason: z.string().nullable(),
});
/** Creates a new version for a class (or the first version of a new class), effective from validFrom. */
export const DeviceClassRuleInput = z.object({
    deviceClass: DeviceClassId,
    displayName: z.string().min(2).max(100),
    validFrom: IsoDate,
    fieldSchema: FieldSchemas,
    duplicateKeys: DuplicateKeys,
    changeReason: z.string().min(3).max(1000),
});
export const DeviceClassRuleList = z.object({ items: z.array(DeviceClassRule) });
export const DeviceClassSummary = z.object({
    deviceClass: DeviceClassId,
    displayName: z.string(),
    currentVersion: z.number().int().nullable(),
    versions: z.number().int(),
});
export const DeviceClassList = z.object({ items: z.array(DeviceClassSummary) });
// ---- Models --------------------------------------------------------------
export const DeviceModelStatus = z.enum(['pending', 'approved', 'withdrawn']);
const Attributes = z.record(z.unknown()).default({});
export const DeviceModelInput = z.object({
    /** CER may register on behalf of a manufacturer; participants may only use their own id. */
    manufacturerId: z.string().optional(),
    deviceClass: DeviceClassId,
    modelNumber: z.string().min(1).max(100),
    name: z.string().min(2).max(200),
    attributes: Attributes,
    effectiveDate: IsoDate.optional(),
});
export const DeviceModel = z.object({
    /** Surrogate id of the model. @metadata technical @source register.device_models.id */
    id: z.string(),
    /** Participant id of the manufacturer. @metadata technical @source register.device_models.manufacturer_id */
    manufacturerId: z.string(),
    /** Legal name of the manufacturer. @metadata business @source register.participants.legal_name via register.device_models.manufacturer_id @transform joined from the manufacturer's participant record */
    manufacturerName: z.string(),
    /** Device class of the model, e.g. battery. @metadata business @source register.device_models.device_class */
    deviceClass: DeviceClassId,
    /** Manufacturer's model number. @metadata business @source register.device_models.model_number */
    modelNumber: z.string(),
    /** Model name. @metadata business @source register.device_models.name */
    name: z.string(),
    /** Approval status: pending, approved or withdrawn. @metadata business @source register.device_models.status */
    status: DeviceModelStatus,
    /** Class-specific facts about the model, validated against the class rule set. @metadata business @source register.device_models.attributes */
    attributes: z.record(z.unknown()),
    /** Version of the device class rule set the attributes were validated against. @metadata business @source register.device_models.rule_version_applied */
    ruleVersionApplied: z.number().int(),
    /** Date from which the model's facts apply. @metadata business @source register.device_models.effective_date */
    effectiveDate: IsoDate,
    /** Number of units registered against the model. @metadata business @source register.model_unit_counts.unit_count @transform left join on model_id, null becomes 0; for another manufacturer's model a participant sees only the units it can access */
    unitCount: z.number().int(),
    /** Optimistic-concurrency version, incremented on every change. @metadata technical @source register.device_models.version */
    version: z.number().int(),
    /** When the model was registered. @metadata technical @source register.device_models.created_at @transform timestamptz as an ISO 8601 UTC string */
    createdAt: z.string(),
    /** When the model was last changed. @metadata technical @source register.device_models.updated_at @transform timestamptz as an ISO 8601 UTC string */
    updatedAt: z.string(),
});
export const DeviceModelList = z.object({ items: z.array(DeviceModel) });
export const DeviceModelSearch = z.object({
    q: z.string().max(200).optional(),
    manufacturerId: z.string().optional(),
    deviceClass: DeviceClassId.optional(),
    status: DeviceModelStatus.optional(),
    /** Approved models of every manufacturer (for registering units against them); default is tenant-scoped. */
    catalogue: z
        .enum(['true', 'false'])
        .transform((v) => v === 'true')
        .optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
/** Provenance every change carries into history: why, and (optionally) the document that supports it. */
const ChangeProvenance = {
    changeReason: z.string().min(3).max(1000),
    evidenceRef: z.string().max(500).optional(),
};
export const DeviceModelStatusChange = z.object({
    status: z.enum(['approved', 'withdrawn']),
    ...ChangeProvenance,
});
// ---- Units ----------------------------------------------------------------
export const DeviceUnitStatus = z.enum(['registered', 'installed', 'decommissioned', 'withdrawn']);
/** C8: set from inspections and enforcement actions, or by CER directly; each change is a unit history version. */
export const UnitComplianceStatus = z.enum(['compliant', 'under_review', 'non_compliant', 'under_enforcement']);
export const DeviceUnitInput = z.object({
    modelId: z.string(),
    /** Defaults to the caller's participant; CER must supply it. */
    ownerParticipantId: z.string().optional(),
    serial: z.string().min(1).max(100),
    batch: z.string().max(100).optional(),
    attributes: Attributes,
    effectiveDate: IsoDate.optional(),
});
export const DeviceUnit = z.object({
    /** Surrogate id of the unit. @metadata technical @source register.device_units.id */
    id: z.string(),
    /** Id of the unit's model. @metadata technical @source register.device_units.model_id */
    modelId: z.string(),
    /** Model number of the unit's model. @metadata business @source register.device_models.model_number via register.device_units.model_id @transform joined from the model */
    modelNumber: z.string(),
    /** Name of the unit's model. @metadata business @source register.device_models.name via register.device_units.model_id @transform joined from the model */
    modelName: z.string(),
    /** Device class of the unit's model. @metadata business @source register.device_models.device_class via register.device_units.model_id @transform joined from the model */
    deviceClass: DeviceClassId,
    /** Participant id of the model's manufacturer. @metadata technical @source register.device_models.manufacturer_id via register.device_units.model_id @transform joined from the model */
    manufacturerId: z.string(),
    /** Legal name of the model's manufacturer. @metadata business @source register.participants.legal_name via register.device_models.manufacturer_id @transform joined from the manufacturer's participant record */
    manufacturerName: z.string(),
    /** Participant id of the unit's current owner. @metadata technical @source register.device_units.owner_participant_id */
    ownerParticipantId: z.string(),
    /** Legal name of the unit's current owner. @metadata business @source register.participants.legal_name via register.device_units.owner_participant_id @transform joined from the owner's participant record */
    ownerName: z.string(),
    /** Manufacturer's serial number of the physical unit. @metadata business @source register.device_units.serial */
    serial: z.string(),
    /** Manufacturing batch, if recorded. @metadata business @source register.device_units.batch */
    batch: z.string().nullable(),
    /** Lifecycle status: registered, installed, decommissioned or withdrawn. @metadata business @source register.device_units.status */
    status: DeviceUnitStatus,
    /** Compliance status, set from inspections and enforcement actions or by CER directly. @metadata business @source register.device_units.compliance_status */
    complianceStatus: UnitComplianceStatus,
    /** Class-specific facts about the unit, validated against the class rule set. @metadata business @source register.device_units.attributes */
    attributes: z.record(z.unknown()),
    /** Version of the device class rule set the attributes were validated against. @metadata business @source register.device_units.rule_version_applied */
    ruleVersionApplied: z.number().int(),
    /** Date from which the unit's facts apply. @metadata business @source register.device_units.effective_date */
    effectiveDate: IsoDate,
    /** Id of the unit's active installation; null when it is not installed. @metadata technical @source derived: register.installations.id of the installation of this unit whose status is active @transform subquery; null when there is none */
    activeInstallationId: z.string().nullable(),
    /** Facts about the unit reported by partner systems through the exchange. @metadata business @source register.device_units.partner_facts @transform non-object values become {} */
    partnerFacts: z.record(z.unknown()),
    /** Optimistic-concurrency version, incremented on every change. @metadata technical @source register.device_units.version */
    version: z.number().int(),
    /** When the unit was registered. @metadata technical @source register.device_units.created_at @transform timestamptz as an ISO 8601 UTC string */
    createdAt: z.string(),
    /** When the unit was last changed. @metadata technical @source register.device_units.updated_at @transform timestamptz as an ISO 8601 UTC string */
    updatedAt: z.string(),
});
export const DeviceUnitList = z.object({ items: z.array(DeviceUnit) });
export const DeviceUnitSearch = z.object({
    q: z.string().max(200).optional(),
    modelId: z.string().optional(),
    ownerParticipantId: z.string().optional(),
    deviceClass: DeviceClassId.optional(),
    status: DeviceUnitStatus.optional(),
    complianceStatus: UnitComplianceStatus.optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
export const DeviceUnitStatusChange = z.object({
    status: z.enum(['decommissioned', 'withdrawn']),
    ...ChangeProvenance,
});
/** CER sets a unit's compliance status directly (e.g. after remediation); `under_enforcement` only follows from issuing an enforcement action. */
export const DeviceUnitComplianceChange = z.object({
    complianceStatus: UnitComplianceStatus.exclude(['under_enforcement']),
    ...ChangeProvenance,
});
/** Moves a unit to another approved participant (e.g. manufacturer → installer → retailer). */
export const DeviceUnitTransfer = z.object({
    newOwnerParticipantId: z.string(),
    ...ChangeProvenance,
});
// ---- Installations --------------------------------------------------------
export const InstallationStatus = z.enum(['active', 'decommissioned']);
export const Nmi = z.string().regex(/^[A-Z0-9]{10,11}$/, 'NMI is 10–11 upper-case letters/digits');
// ---- Geocoding (known-address register) ------------------------------------
export const GeocodeSource = z.enum(['sim', 'gnaf', 'manual']);
export const GeocodeAccuracy = z.enum(['rooftop', 'street', 'locality']);
/** Australia incl. Lord Howe (~159°E) and Macquarie (~54.6°S) Islands. */
export const Point = z.object({
    /** Latitude in decimal degrees. @metadata business */
    lat: z.number().min(-55, 'latitude is outside Australia').max(-9, 'latitude is outside Australia'),
    /** Longitude in decimal degrees. @metadata business */
    lon: z.number().min(112, 'longitude is outside Australia').max(160, 'longitude is outside Australia'),
});
export const Location = Point.extend({
    /** How the location was geocoded: sim, gnaf or manual. @metadata technical */
    source: GeocodeSource,
    /** Precision of the location: rooftop, street or locality. @metadata technical */
    accuracy: GeocodeAccuracy,
});
/** `not_recorded`: registered before RegiGov geocoded installations (never backfilled). */
export const GeocodeStatus = z.enum(['geocoded', 'not_geocoded', 'not_recorded']);
export const KnownAddress = Address.extend({ id: z.string(), lat: z.number(), lon: z.number(), source: GeocodeSource, accuracy: GeocodeAccuracy });
export const AddressSearch = z.object({ q: z.string().trim().min(2).max(200), limit: z.coerce.number().int().min(1).max(20).default(10) });
export const KnownAddressList = z.object({ items: z.array(KnownAddress) });
export const InstallationInput = z.object({
    unitId: z.string(),
    /** Defaults to the caller's participant; CER must supply it. */
    installerParticipantId: z.string().optional(),
    nmi: Nmi,
    address: Address,
    commissionedOn: IsoDate,
    attributes: Attributes,
});
export const Installation = z.object({
    /** Surrogate id of the installation. @metadata technical @source register.installations.id */
    id: z.string(),
    /** Id of the installed unit. @metadata technical @source register.installations.unit_id */
    unitId: z.string(),
    /** Serial number of the installed unit. @metadata business @source register.device_units.serial via register.installations.unit_id @transform joined from the unit */
    serial: z.string(),
    /** Id of the installed unit's model. @metadata technical @source register.device_units.model_id via register.installations.unit_id @transform joined from the unit */
    modelId: z.string(),
    /** Model number of the installed unit. @metadata business @source register.device_models.model_number via register.device_units.model_id @transform joined from the model */
    modelNumber: z.string(),
    /** Device class of the installed unit. @metadata business @source register.device_models.device_class via register.device_units.model_id @transform joined from the model */
    deviceClass: DeviceClassId,
    /** Participant id of the installed unit's owner. @metadata technical @source register.device_units.owner_participant_id via register.installations.unit_id @transform joined from the unit */
    ownerParticipantId: z.string(),
    /** Participant id of the installer. @metadata technical @source register.installations.installer_participant_id */
    installerParticipantId: z.string(),
    /** Legal name of the installer. @metadata business @source register.participants.legal_name via register.installations.installer_participant_id @transform joined from the installer's participant record */
    installerName: z.string(),
    /** National Metering Identifier of the connection point. @metadata business @source register.installations.nmi */
    nmi: z.string(),
    /** Site address of the installation. @metadata business @source register.installations.address @transform parsed through Address */
    address: Address,
    /** Installation status: active or decommissioned. @metadata business @source register.installations.status */
    status: InstallationStatus,
    /** Date the installation was commissioned. @metadata business @source register.installations.commissioned_on */
    commissionedOn: IsoDate,
    /** Date the installation was decommissioned; null while active. @metadata business @source register.installations.decommissioned_on */
    decommissionedOn: IsoDate.nullable(),
    /** Class-specific facts about the installation, validated against the class rule set. @metadata business @source register.installations.attributes @transform non-object values become {} */
    attributes: z.record(z.unknown()),
    /** Facts about the installation reported by partner systems through the exchange. @metadata business @source register.installations.partner_facts @transform non-object values become {} */
    partnerFacts: z.record(z.unknown()),
    /** Version of the device class rule set the attributes were validated against. @metadata business @source register.installations.rule_version_applied */
    ruleVersionApplied: z.number().int(),
    /** Whether the site address was geocoded; not_recorded for installations registered before geocoding (never backfilled). @metadata technical @source register.installations.geocode_status @transform null becomes not_recorded */
    geocodeStatus: GeocodeStatus,
    /** Geocoded position of the site. @metadata business @source register.installations.lat @source register.installations.lon @source register.installations.geocode_source @source register.installations.geocode_accuracy @transform combined into one object; null unless all four columns are set */
    location: Location.nullable(),
    /** Id of the matched known address. @metadata technical @source register.installations.address_id @transform bigint to string */
    addressId: z.string().nullable(),
    /** Optimistic-concurrency version, incremented on every change. @metadata technical @source register.installations.version */
    version: z.number().int(),
    /** When the installation was registered. @metadata technical @source register.installations.created_at @transform timestamptz as an ISO 8601 UTC string */
    createdAt: z.string(),
    /** When the installation was last changed. @metadata technical @source register.installations.updated_at @transform timestamptz as an ISO 8601 UTC string */
    updatedAt: z.string(),
});
export const InstallationList = z.object({ items: z.array(Installation) });
export const InstallationSearch = z.object({
    q: z.string().max(200).optional(),
    unitId: z.string().optional(),
    installerParticipantId: z.string().optional(),
    status: InstallationStatus.optional(),
    geocodeStatus: GeocodeStatus.optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
export const InstallationDecommission = z.object({
    decommissionedOn: IsoDate,
    ...ChangeProvenance,
});
// ---- History (bitemporal versions, PLAN §2.4) -------------------------------
/**
 * One recorded version of a model, unit or installation. `validFrom`/`validTo`
 * say when the facts were true; `recordedAt` says when CER learned them. A
 * correction never rewrites a row: it adds a new one pointing at the superseded
 * version via `correctionOf`, and the superseded row is flagged `supersededBy`.
 */
export const DeviceVersion = z.object({
    entityKind: EntityKind,
    entityId: z.string(),
    versionId: z.number().int(),
    version: z.number().int(),
    validFrom: z.string(),
    validTo: z.string().nullable(),
    recordedAt: z.string(),
    changedBy: z.string().nullable(),
    changedByName: z.string().nullable(),
    changeReason: z.string().nullable(),
    sourceChannel: z.string(),
    sourceParticipantId: z.string().nullable(),
    sourceParticipantName: z.string().nullable(),
    sourceReference: z.string().nullable(),
    evidenceRef: z.string().nullable(),
    correctionOf: z.number().int().nullable(),
    supersededBy: z.number().int().nullable(),
    /** Fields whose value differs from the version this one follows (or corrects). */
    changes: z.array(z.string()),
    data: z.record(z.unknown()),
});
export const DeviceHistory = z.object({
    entityKind: EntityKind,
    entityId: z.string(),
    versions: z.array(DeviceVersion),
});
export const AsAtQuery = z.object({
    /** When the facts were true. Defaults to now. */
    asOf: z.string().datetime({ offset: true }).optional(),
    /** What CER had recorded by this time. Defaults to now. */
    knownAt: z.string().datetime({ offset: true }).optional(),
});
/** A unit as it stood at a point in time, with the model, installation and owner that applied then. */
export const DeviceUnitAsAt = z.object({
    asOf: z.string(),
    knownAt: z.string(),
    unit: DeviceVersion,
    model: DeviceVersion.nullable(),
    installation: DeviceVersion.nullable(),
    owner: ParticipantVersion.nullable(),
});
const Correction = {
    /** The version (its history #id) whose facts were wrong. */
    correctionOf: z.number().int().positive(),
    /** When the corrected facts were actually true; defaults to the superseded version's validFrom. */
    validFrom: z.string().datetime({ offset: true }).optional(),
    ...ChangeProvenance,
};
export const DeviceModelCorrection = z.object({
    ...Correction,
    changes: z
        .object({
        modelNumber: z.string().min(1).max(100),
        name: z.string().min(2).max(200),
        attributes: z.record(z.unknown()),
    })
        .partial(),
});
export const DeviceUnitCorrection = z.object({
    ...Correction,
    changes: z
        .object({
        serial: z.string().min(1).max(100),
        batch: z.string().max(100).nullable(),
        ownerParticipantId: z.string(),
        attributes: z.record(z.unknown()),
    })
        .partial(),
});
export const InstallationCorrection = z.object({
    ...Correction,
    changes: z
        .object({
        nmi: Nmi,
        address: Address,
        commissionedOn: IsoDate,
        decommissionedOn: IsoDate.nullable(),
        attributes: z.record(z.unknown()),
        /** Set by a CER officer for a not-geocoded installation; the address joins the register as `manual`. */
        location: Point.extend({ accuracy: GeocodeAccuracy }),
    })
        .partial(),
});
// ---- Rejection log --------------------------------------------------------
export const RejectionReason = z.object({
    field: z.string().optional(),
    code: z.string(),
    message: z.string(),
});
export const SubmissionRejection = z.object({
    id: z.number().int(),
    entityKind: EntityKind,
    deviceClass: z.string().nullable(),
    participantId: z.string().nullable(),
    participantName: z.string().nullable(),
    submittedBy: z.string().nullable(),
    submittedByName: z.string().nullable(),
    sourceChannel: z.string(),
    payload: z.record(z.unknown()),
    reasons: z.array(RejectionReason),
    ruleVersion: z.number().int().nullable(),
    createdAt: z.string(),
});
export const SubmissionRejectionList = z.object({ items: z.array(SubmissionRejection) });
export const RejectionSearch = z.object({
    entityKind: EntityKind.optional(),
    participantId: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
//# sourceMappingURL=devices.js.map